import { Compass, Home, Fingerprint, Sparkles } from "lucide-react";
import { FadeUp, SectionTag } from "./Reveal";

const IMG =
    "https://images.unsplash.com/photo-1544111795-fe8b9def73f6?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA2MDV8MHwxfHNlYXJjaHwyfHxuZXclMjB5b3JrJTIwY2l0eSUyMHNreWxpbmUlMjBkYXJrJTIwbW9vZHklMjBhdG1vc3BoZXJpYyUyMG1pc3QlMjBuaWdodCUyMHBob3RvZ3JhcGh5fGVufDB8fHx8MTc4OTczNDkxOXww&ixlib=rb-4.1.0&q=85";

const FEATURES = [
    { icon: Compass, title: "Explore a living city", body: "Districts, venues, and hidden corners built for wandering — not waypoints." },
    { icon: Home, title: "Own personal spaces", body: "Apartments and hangouts you make your own, and invite people into." },
    { icon: Fingerprint, title: "Shape your identity", body: "Deep customization of who you are and how the city sees you." },
    { icon: Sparkles, title: "Create your own stories", body: "Activities, events, and player-driven experiences — no combat required." },
];

export const FirstWorld = () => (
    <section id="world" className="relative py-24 sm:py-32 bg-[#0D0D12]/40">
        <div className="max-w-7xl mx-auto px-5 sm:px-8">
            <SectionTag index="02" label="Our First World" />

            <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-16 items-start">
                <FadeUp className="lg:col-span-7">
                    <div className="relative group" data-testid="first-world-image-frame">
                        <span className="absolute -top-3 -left-3 w-8 h-8 border-t border-l border-[#A855F7]/70 z-10" />
                        <span className="absolute -bottom-3 -right-3 w-8 h-8 border-b border-r border-[#A855F7]/70 z-10" />
                        <div className="overflow-hidden">
                            <img
                                src={IMG}
                                alt="Moody modern city skyline at dusk"
                                loading="lazy"
                                className="w-full aspect-[4/3] object-cover img-treatment scale-105 group-hover:scale-110 transition-transform duration-[1600ms] ease-out"
                            />
                        </div>
                        <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-transparent pointer-events-none" />
                        <p className="mt-4 font-mono-meta text-[10px] tracking-[0.3em] uppercase text-slate-500">
                            Fig. 01 — Concept photography, representative of setting
                        </p>
                    </div>
                </FadeUp>

                <div className="lg:col-span-5">
                    <FadeUp>
                        <h2
                            data-testid="first-world-heading"
                            className="font-display font-bold text-2xl sm:text-3xl lg:text-4xl text-white leading-tight"
                        >
                            A modern city.
                            <br />
                            <span className="font-serif-accent italic font-medium text-[#C084FC]">
                                A life you choose.
                            </span>
                        </h2>
                    </FadeUp>
                    <FadeUp delay={0.1}>
                        <p className="mt-6 text-base text-slate-300 leading-relaxed">
                            Our first project is a large-scale social MMO set in a realistic
                            modern American city — inspired by places like New York. No chosen
                            ones, no raid bosses. Just a dense, persistent city built around
                            the people living in it.
                        </p>
                    </FadeUp>

                    <div className="mt-10 space-y-0 border-t border-white/10">
                        {FEATURES.map((f, i) => (
                            <FadeUp key={f.title} delay={0.1 + i * 0.08}>
                                <div
                                    data-testid={`first-world-feature-${i + 1}`}
                                    className="group flex gap-5 py-6 border-b border-white/10"
                                >
                                    <f.icon className="w-5 h-5 mt-1 shrink-0 text-[#A855F7] group-hover:scale-110 transition-transform duration-300" />
                                    <div>
                                        <h3 className="font-display font-semibold text-white text-base sm:text-lg">
                                            {f.title}
                                        </h3>
                                        <p className="mt-1.5 text-sm text-slate-400 leading-relaxed">
                                            {f.body}
                                        </p>
                                    </div>
                                </div>
                            </FadeUp>
                        ))}
                    </div>

                    <FadeUp delay={0.3}>
                        <p className="mt-8 font-mono-meta text-[10px] tracking-[0.25em] uppercase text-slate-500 leading-loose">
                            Setting — Modern American Metropolis
                            <br />
                            Genre — Social MMO&nbsp;&nbsp;/&nbsp;&nbsp;Combat — None
                        </p>
                    </FadeUp>
                </div>
            </div>
        </div>
    </section>
);
